import { motion, AnimatePresence } from "framer-motion";
import {
  Scissors, Sparkles, Check, Loader2,
  AlertCircle, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CharProgressBar, ClickToEditPost } from "@/components/click-to-edit-post";
import { GenerationProgress } from "@/components/generation-progress";
import { VoiceMatchRing } from "@/components/voice-match-ring";
import { PostGeneratorResultPanel } from "@/components/post-generator-result-panel";
import { PostGeneratorAllPlatforms } from "@/components/post-generator-all-platforms";

type ErrorCategory = "rate-limit" | "idea-too-short" | "ai-failure" | "generic";

function classifyError(msg: string): ErrorCategory {
  if (/too many request|rate limit|wait a moment/i.test(msg)) return "rate-limit";
  if (/needs a bit more context|at least a few words|describe what happened/i.test(msg)) return "idea-too-short";
  if (/ai returned|empty response|couldn't generate|couldn't process/i.test(msg)) return "ai-failure";
  return "generic";
}

const ERROR_META: Record<ErrorCategory, { title: string; hint: string; iconClass: string; panelClass: string }> = {
  "rate-limit": {
    title: "You're generating too fast",
    hint: "Wait about 60 seconds and try again — there's a limit of 10 generations per minute.",
    iconClass: "text-amber-400",
    panelClass: "bg-amber-500/5 border-amber-500/20",
  },
  "idea-too-short": {
    title: "Your idea needs more context",
    hint: "Describe what happened, what you learned, or the point you want to make — even a sentence or two is enough.",
    iconClass: "text-yellow-400",
    panelClass: "bg-yellow-500/5 border-yellow-500/20",
  },
  "ai-failure": {
    title: "The AI couldn't process that",
    hint: "Try rephrasing your idea or adding a bit more detail. Sometimes a different angle helps.",
    iconClass: "text-destructive",
    panelClass: "bg-destructive/5 border-destructive/20",
  },
  "generic": {
    title: "Generation failed",
    hint: "",
    iconClass: "text-destructive",
    panelClass: "bg-destructive/5 border-destructive/20",
  },
};

interface DraftItem {
  content: string;
  score: number;
  id: string;
  styleNote?: string;
}

interface VoiceAnalysis {
  sampleCount?: number;
  analysis?: Record<string, unknown> | null;
}

export interface PostGeneratorOutputProps {
  mode: "single" | "all";
  platform: string;
  platformLimit: number;
  wordCount: number;
  charCount: number;
  sessionId: string;
  result: { content: string; score: number; id: string } | null;
  drafts: DraftItem[] | null;
  isSingleLoading: boolean;
  generateError: string | null;
  generateAllError: string | null;
  isAllLoading: boolean;
  allPlatformResults: Record<string, string> | null;
  draftContent: string;
  setDraftContent: (v: string) => void;
  editedAllResults: Record<string, string>;
  setEditedAllResults: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  activeDraftIdx: number;
  setActiveDraftIdx: (i: number) => void;
  updateActiveDraftContent: (v: string) => void;
  commitActiveDraft: () => void;
  currentIdea: string;
  handleGenerate: () => void;
  handleRefine: (action: string) => void;
  handleTrimToLimit: () => void;
  handleTrimAllPlatform: (p: string) => void;
  handleShare: (content: string, key?: string) => void;
  handleCopy: (content: string, key?: string) => void;
  handleSaveToVault: () => void;
  handlePostFeedback: (rating: "up" | "down") => void;
  handleUndoPush: (prev: string) => void;
  handleUndo: () => void;
  handlePostKeyDown: (e: React.KeyboardEvent) => void;
  undoStack: string[];
  savedToVault: boolean;
  copied: string | null;
  setCopied: (v: string | null) => void;
  feedbackGiven: "up" | "down" | null;
  feedbackMessage: string | null;
  feedbackMutationPending: boolean;
  coachTip: string | null;
  coachLoading: boolean;
  coachDismissed: boolean;
  setCoachDismissed: (v: boolean) => void;
  voiceDnaOpen: boolean;
  setVoiceDnaOpen: (v: boolean) => void;
  voiceProfile: VoiceAnalysis | null | undefined;
  voiceSampleCount: number;
  voiceNudgeDismissed: boolean;
  setVoiceNudgeDismissed: (v: boolean) => void;
  isTrimming: boolean;
  trimmingPlatform: string | null;
  createIdeaMutationPending: boolean;
  multiVariant: boolean;
}

export function PostGeneratorOutput({
  mode,
  platform,
  platformLimit,
  wordCount,
  charCount,
  sessionId,
  result,
  drafts,
  isSingleLoading,
  generateError,
  generateAllError,
  isAllLoading,
  allPlatformResults,
  draftContent,
  setDraftContent,
  editedAllResults,
  setEditedAllResults,
  activeDraftIdx,
  setActiveDraftIdx,
  updateActiveDraftContent,
  commitActiveDraft,
  currentIdea,
  handleGenerate,
  handleRefine,
  handleTrimToLimit,
  handleTrimAllPlatform,
  handleShare,
  handleCopy,
  handleSaveToVault,
  handlePostFeedback,
  handleUndoPush,
  handleUndo,
  handlePostKeyDown,
  undoStack,
  savedToVault,
  copied,
  setCopied,
  feedbackGiven,
  feedbackMessage,
  feedbackMutationPending,
  coachTip,
  coachLoading,
  coachDismissed,
  setCoachDismissed,
  voiceDnaOpen,
  setVoiceDnaOpen,
  voiceProfile,
  voiceSampleCount,
  voiceNudgeDismissed,
  setVoiceNudgeDismissed,
  isTrimming,
  trimmingPlatform,
  createIdeaMutationPending,
  multiVariant,
}: PostGeneratorOutputProps) {
  const activeContent = drafts ? (drafts[activeDraftIdx]?.content ?? "") : draftContent;
  return (
<motion.div 
  initial={{ opacity: 0, x: 20 }}
  animate={{ opacity: 1, x: 0 }}
  className="flex flex-col"
>
  {mode === "single" ? (
    <div className="bg-card border border-border rounded-3xl p-6 shadow-xl flex flex-col min-h-[420px] relative overflow-hidden">
      {generateError && !isSingleLoading ? (
        (() => {
          const cat = classifyError(generateError);
          const meta = ERROR_META[cat];
          return (
            <div className={`flex-1 flex flex-col items-center justify-center gap-5 text-center py-8 rounded-2xl border ${meta.panelClass}`}>
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center">
                <AlertCircle className={`w-7 h-7 ${meta.iconClass}`} />
              </div>
              <div className="space-y-2 max-w-xs">
                <p className="font-semibold text-foreground text-lg">{meta.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{generateError}</p>
                {meta.hint && <p className="text-xs text-muted-foreground/80 leading-relaxed italic">{meta.hint}</p>}
              </div>
              {cat !== "rate-limit" && (
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={!currentIdea.trim()}
                  className="rounded-xl gap-2"
                >
                  <RefreshCw className="w-4 h-4" /> Try Again
                </Button>
              )}
            </div>
          );
        })()
      ) : !result && !drafts && !isSingleLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground opacity-50">
          <Sparkles className="w-16 h-16 mb-4" />
          <p className="font-medium text-lg">Your magic draft will appear here</p>
        </div>
      ) : (
        <>
          {/* Draft Battle Tab Row */}
          <AnimatePresence>
            {drafts && !isSingleLoading && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="flex items-stretch gap-1 mb-4 p-1 rounded-xl bg-secondary/30 border border-border/40"
              >
                {drafts.map((d, i) => (
                  <motion.button
                    key={i}
                    data-testid={`draft-tab-${i}`}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.07 }}
                    onClick={() => setActiveDraftIdx(i)}
                    className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 px-2 rounded-lg text-sm font-semibold transition-all ${
                      activeDraftIdx === i
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{String.fromCharCode(65 + i)}</span>
                      <span className={`text-[10px] font-mono tabular-nums ${d.score >= 80 ? "text-emerald-400" : d.score >= 60 ? "text-amber-400" : "text-muted-foreground/60"}`}>
                        {d.score}%
                      </span>
                    </div>
                    {d.styleNote && (
                      <span className={`text-[10px] font-normal leading-tight max-w-[80px] text-center truncate ${activeDraftIdx === i ? "text-muted-foreground" : "text-muted-foreground/50"}`}>
                        {d.styleNote}
                      </span>
                    )}
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-xl">
                {drafts ? (multiVariant ? "Choose a version" : "Choose a draft") : "Draft output"}
              </h2>
              {(result || drafts) && !isSingleLoading && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {wordCount} words
                  </span>
                  {platform === "twitter" && charCount > 280 && !drafts && (
                    <button
                      data-testid="trim-to-limit-btn"
                      onClick={handleTrimToLimit}
                      disabled={isTrimming}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/20 text-[11px] font-semibold transition-colors disabled:opacity-50"
                    >
                      {isTrimming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                      Trim to 280
                    </button>
                  )}
                </div>
              )}
            </div>
            {(result || drafts) && !isSingleLoading && (
              <div className="scale-75 origin-top-right -mt-2 -mr-2 shrink-0">
                <VoiceMatchRing
                  score={drafts ? (drafts[activeDraftIdx]?.score ?? 0) : result!.score}
                  size={80}
                  strokeWidth={6}
                />
              </div>
            )}
          </div>

          <div className="flex-1 min-h-[180px] relative">
            {isSingleLoading ? (
              <GenerationProgress isActive={true} />
            ) : (
              <>
                <ClickToEditPost
                  value={activeContent}
                  onChange={(v) => {
                    if (drafts) {
                      updateActiveDraftContent(v);
                    } else {
                      setDraftContent(v);
                    }
                  }}
                  onKeyDown={handlePostKeyDown}
                  placeholder={result || drafts ? "" : "Your generated post will appear here — click to edit once generated"}
                  platform={platform}
                  sessionId={sessionId}
                  onUndoPush={handleUndoPush}
                />
                {(result || drafts) && platformLimit > 0 && (
                  <CharProgressBar charCount={charCount} limit={platformLimit} />
                )}
              </>
            )}
          </div>

          {/* Use this draft button — only in battle mode */}
          <AnimatePresence>
            {drafts && !isSingleLoading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="pt-4"
              >
                <Button
                  data-testid="use-draft-btn"
                  onClick={commitActiveDraft}
                  className="w-full rounded-xl h-12 gap-2 font-semibold"
                  style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))", color: "white" }}
                >
                  <Check className="w-4 h-4" />
                  Use Version {String.fromCharCode(65 + activeDraftIdx)}
                  {drafts?.[activeDraftIdx]?.styleNote && (
                    <span className="font-normal opacity-80 text-sm">— {drafts[activeDraftIdx]!.styleNote}</span>
                  )}
                </Button>
                <p className="text-center text-xs text-muted-foreground/60 mt-2">
                  Switch tabs to compare — edits are saved per version.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <PostGeneratorResultPanel
            result={result}
            isSingleLoading={isSingleLoading}
            drafts={drafts}
            undoStack={undoStack}
            savedToVault={savedToVault}
            draftContent={draftContent}
            platform={platform}
            copied={copied}
            setCopied={setCopied}
            handleCopy={handleCopy}
            handleShare={handleShare}
            handleRefine={handleRefine}
            handleUndo={handleUndo}
            handleSaveToVault={handleSaveToVault}
            createIdeaMutationPending={createIdeaMutationPending}
            feedbackGiven={feedbackGiven}
            feedbackMessage={feedbackMessage}
            feedbackMutationPending={feedbackMutationPending}
            handlePostFeedback={handlePostFeedback}
            coachTip={coachTip}
            coachLoading={coachLoading}
            coachDismissed={coachDismissed}
            setCoachDismissed={setCoachDismissed}
            voiceSampleCount={voiceSampleCount}
            voiceNudgeDismissed={voiceNudgeDismissed}
            setVoiceNudgeDismissed={setVoiceNudgeDismissed}
            voiceDnaOpen={voiceDnaOpen}
            setVoiceDnaOpen={setVoiceDnaOpen}
            voiceProfile={voiceProfile}
            isTrimming={isTrimming}
          />
        </>
      )}
    </div>
  ) : (
    <PostGeneratorAllPlatforms
      generateAllError={generateAllError}
      isAllLoading={isAllLoading}
      allPlatformResults={allPlatformResults}
      editedAllResults={editedAllResults}
      setEditedAllResults={setEditedAllResults}
      savedToVault={savedToVault}
      currentIdea={currentIdea}
      copied={copied}
      trimmingPlatform={trimmingPlatform}
      createIdeaMutationPending={createIdeaMutationPending}
      handleGenerate={handleGenerate}
      handleSaveToVault={handleSaveToVault}
      handleCopy={handleCopy}
      handleShare={handleShare}
      handleTrimAllPlatform={handleTrimAllPlatform}
    />
  )}
</motion.div>
  );
}
